import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

const ADMIN_TOKEN_KEY = 'admin_token';

interface AdminAuthState {
  isAuthenticated: boolean;
  token: string | null;
  loading: boolean;
}

interface AdminAuthContextType extends AdminAuthState {
  login: (token: string) => void;
  logout: () => void;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AdminAuthState>({
    isAuthenticated: false,
    token: null,
    loading: true
  });

  const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';

  // 验证 token 是否有效
  const verifyToken = async (token: string): Promise<boolean> => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      return result.success === true;
    } catch (error) {
      console.error('Token verification failed:', error);
      return false;
    }
  };

  // 初始化时检查本地存储的 token
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      
      if (storedToken) {
        const isValid = await verifyToken(storedToken);
        if (isValid) {
          setAuthState({
            isAuthenticated: true,
            token: storedToken,
            loading: false
          });
        } else {
          // Token 无效，清除本地存储
          localStorage.removeItem(ADMIN_TOKEN_KEY);
          setAuthState({
            isAuthenticated: false,
            token: null,
            loading: false
          });
        }
      } else {
        setAuthState({
          isAuthenticated: false,
          token: null,
          loading: false
        });
      }
    };

    initAuth();
  }, []);

  // 登录
  const login = (token: string) => {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
    setAuthState({
      isAuthenticated: true,
      token,
      loading: false
    });
  };

  // 登出
  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthState({
      isAuthenticated: false,
      token: null,
      loading: false
    });
    // 重定向到管理员登录页面
    window.location.href = '/admin';
  };

  // 发送认证请求
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(authState.token ? { 'Authorization': `Bearer ${authState.token}` } : {}),
      ...(options.headers || {})
    };

    return fetch(url, {
      ...options,
      headers
    });
  };

  const contextValue: AdminAuthContextType = {
    ...authState,
    login,
    logout,
    authenticatedFetch
  };

  return (
    <AdminAuthContext.Provider value={contextValue}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// Hook for using the admin auth context
export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
